import { Button, Dialog, XIcon } from "@au-schein-de/au-ui-library";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TransformedImage } from "~/components/transformed-image.component";
import { expressCourierProviderName } from "~/tooling/express-courier-operating-hours";

const DELIVERY_SRC = "/images/delivery.png";
/** Header cards omit `provider` and keep the original Lieferando line. */
const DEFAULT_DIALOG_PROVIDER = "Lieferando";

interface ExpressCourierInfoDialogProps {
	trigger: ReactNode;
	provider?: string | null;
}

export function ExpressCourierInfoDialog({
	trigger,
	provider,
}: ExpressCourierInfoDialogProps) {
	const { t } = useTranslation("checkout");
	const providerName =
		expressCourierProviderName(provider) ?? DEFAULT_DIALOG_PROVIDER;

	return (
		<Dialog.Root>
			<Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
			<Dialog.Content
				mobileVariant="fullWidth"
				withCloseButton={false}
				className="bottom-0 top-auto w-full translate-y-[0%] rounded-t-xl p-0 md:bottom-auto md:top-[50%] md:min-h-[485px] md:w-[570px] md:max-w-[570px] md:translate-y-[-50%] md:rounded-xl md:overflow-hidden"
			>
				<Dialog.Close className="absolute right-4 top-4 cursor-pointer opacity-70 transition-opacity hover:opacity-100">
					<XIcon.Raw className="h-6 w-6 text-gray-700" />
					<span className="sr-only">{t("expressCourier.modal.close")}</span>
				</Dialog.Close>
				<div className="flex h-full flex-col gap-4 md:gap-6 px-4 pb-4 pt-14 md:px-0 md:pb-0 md:pt-0">
					<TransformedImage
						src={DELIVERY_SRC}
						alt=""
						width={96}
						height={96}
						loading="lazy"
						className="size-24 shrink-0 object-contain"
					/>
					<div className="flex flex-col gap-3">
						<Dialog.Title className="text-h4 text-gray-700">
							{t("expressCourier.modal.title")}
						</Dialog.Title>
						<Dialog.Description asChild>
							<p className="text-body whitespace-pre-line text-gray-700">
								{t("expressCourier.modal.content", { provider: providerName })}
							</p>
						</Dialog.Description>
					</div>
					<Dialog.Close asChild className="mt-auto">
						<Button
							size="md"
							variant="primary"
							className="h-14 w-full md:w-[490px]"
						>
							{t("expressCourier.modal.gotIt")}
						</Button>
					</Dialog.Close>
				</div>
			</Dialog.Content>
		</Dialog.Root>
	);
}
